import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { AuthProvider, useAuth, Permission, UserRole } from './contexts/AuthContext';
import { canEditEmployees } from './utils/roleUtils';
import { 
  Users, 
  Calendar, 
  Eye, 
  BarChart3, 
  Plus, 
  Search, 
  LogOut,
  Building2,
  UserCheck,
  ClipboardList,
  TrendingUp,
  AlertCircle,
  Database,
  RefreshCcw,
  Bug,
  Home,
  Settings,
  Bell,
  MessageSquare,
  Phone,
  Mail,
  Award,
  Clock,
  MapPin,
  Star,
  CheckCircle,
  XCircle,
  UserPlus,
  Filter,
  Download,
  Bot
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './components/ui/sheet';
import { Label } from './components/ui/label';
import { Toaster } from './components/ui/toaster';
import { toast } from './hooks/use-toast';
import { apiService } from './services/apiService';
import { AuthValidation } from './utils/authValidation';
import { AbsenceApprovalTest } from './utils/absenceApprovalTest';
import { AuthTestSuite } from './utils/authTestSuite';
import AIChatWidget from './components/AIChatWidget';
import AIChatPage from './components/AIChatPage';
import AIAssistedAbsenceRequest from './components/AIAssistedAbsenceRequest';
import AbsenceManagement from './components/AbsenceManagement';
import AIAssistedEmployeeLookup from './components/AIAssistedEmployeeLookup';
import AIAssistedObservation from './components/AIAssistedObservation';
import EmployeeManagement from './components/EmployeeManagement';
import EmployeeProfile from './components/EmployeeProfile';
import ResponsiveLayout from './components/ResponsiveLayout';
import ResponsiveCard from './components/ResponsiveCard';
import ResponsiveGrid from './components/ResponsiveGrid';
import TouchButton from './components/TouchButton';
import AccessibilityProvider from './components/AccessibilityProvider';
import AccessibilitySettings from './components/AccessibilitySettings';
import GroupMappingManagement from './components/GroupMappingManagement';
import AzureConfigSettings from './components/AzureConfigSettings';
import { SkipLink } from './components/AccessibleForm';
import { PermissionGuard } from './components/PermissionGuard';
import EmployeeCombobox from './components/EmployeeCombobox';
import './App.css';
import './accessibility.css';

function App() {
  return (
    <AccessibilityProvider>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <SkipLink />
          <AuthenticatedTemplate>
            <AuthenticatedApp />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <UnauthenticatedApp />
          </UnauthenticatedTemplate>
        </div>
      </AuthProvider>
    </AccessibilityProvider>
  );
}

function UnauthenticatedApp() {
  const { instance } = useMsal();
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    try {
      setError(null);
      await instance.loginRedirect({
        scopes: [`api://${process.env.REACT_APP_APP_CLIENT_ID}/user_impersonation`, 'openid', 'profile', 'email']
      });
    } catch (error) {
      setError(error.message || 'Login failed. Please check your Azure AD configuration.');
      try {
        await instance.loginPopup({
          scopes: [`api://${process.env.REACT_APP_APP_CLIENT_ID}/user_impersonation`, 'openid', 'profile', 'email']
        });
      } catch (popupError) {
        setError('Both redirect and popup login failed. Please check Azure AD configuration.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <ResponsiveCard className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm" padding="lg">
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
              <Building2 className="w-8 h-8 text-black" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">PeoplePulse</h1>
              <p className="text-gray-600">Memphis Business Academy</p>
              <p className="text-sm text-gray-500 mt-1">HR Management System</p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
                <p className="text-red-600 text-xs mt-2">Check Azure AD redirect URI configuration</p>
              </div>
            )}
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Sign in with your Microsoft account to access the HR management system</p>
              <TouchButton 
                onClick={handleLogin} 
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-medium"
                size="default"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Sign in with Microsoft
              </TouchButton>
            </div>
          </div>
        </ResponsiveCard>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { instance, accounts } = useMsal();
  const { user: authUser, roles, permissions, hasPermission, hasRole, isLoading: authLoading, error: authError } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [schools, setSchools] = useState([]);
  const [addingSchool, setAddingSchool] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', abbreviation: '', school_number: '' });
  const [loading, setLoading] = useState(false);

  // Selection and drawer state
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeEmployee, setActiveEmployee] = useState(null);

  // Bulk assign state
  const [bulkSchoolId, setBulkSchoolId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const currentUser = accounts[0];
  // Use roles from AuthContext (resolved from Azure AD groups) with fallback to token claims
  const userRoles = roles.length > 0 ? roles : (currentUser?.idTokenClaims?.roles || []);
  // Check edit permission using the new permission system
  const canEdit = hasPermission('edit_employees') || canEditEmployees(userRoles);

  useEffect(() => {
    loadDashboardData();
    loadSchools();
    loadNotifications();
  }, []);

  useEffect(() => {
    if (currentView === 'employees') {
      loadSchools();
      loadEmployees();
    }
    if (currentView === 'dashboard') {
      loadDashboardData();
    }
  }, [currentView]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, employeesData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getEmployees(0, 50, '', selectedSchool)
      ]);
      setEmployees(employeesData);
      setDashboardStats(statsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEmployees(0, 50, searchTerm, selectedSchool);
      setEmployees(data);
    } catch (e) {
      console.warn('Failed to refresh employees', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSchools = async () => {
    try {
      const list = await apiService.getSchools();
      setSchools(list);
    } catch (e) {
      console.warn('Failed to load schools', e);
      toast({ title: 'Failed to load schools', description: e.message || 'Please try again.' });
    }
  };

  const loadNotifications = async () => {
    try {
      const [notifList, unreadData] = await Promise.all([
        apiService.getNotifications(10),
        apiService.getUnreadNotificationCount()
      ]);
      setNotifications(notifList);
      setUnreadCount(unreadData.count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  // Set up polling for notifications (fallback if WebSocket fails)
  useEffect(() => {
    const notificationPollingInterval = setInterval(() => {
      loadNotifications();
    }, 30000); // Poll every 30 seconds

    return () => {
      clearInterval(notificationPollingInterval);
    };
  }, []);

  const handleLogout = () => { instance.logoutRedirect(); };

  const searchEmployees = async (term) => {
    try {
      setLoading(true);
      const data = await apiService.getEmployees(0, 50, term, selectedSchool);
      setEmployees(data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchEmployees(searchTerm);
    setSuggestions([]);
  };

  const onSearchChange = async (val) => {
    setSearchTerm(val);
    if (val && val.length >= 2) {
      try {
        const res = await apiService.getEmployeeSuggestions(val);
        setSuggestions(res.items || []);
      } catch (e) {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const onSelectSuggestion = (s) => {
    const term = s.email || `${s.first_name} ${s.last_name}`;
    setSearchTerm(term);
    setSuggestions([]);
    searchEmployees(term);
  };

  const onSchoolChange = async (e) => {
    const key = e.target.value;
    setSelectedSchool(key);
    try {
      setLoading(true);
      const data = await apiService.getEmployees(0, 50, searchTerm, key);
      setEmployees(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAddSchool = async () => {
    try {
      await apiService.addSchool(newSchool);
      setAddingSchool(false);
      setNewSchool({ name: '', abbreviation: '', school_number: '' });
      await loadSchools();
      toast({ title: 'School added', description: `${newSchool.name} has been added.` });
    } catch (e) {
      console.error('Add school failed', e);
      toast({ title: 'Add school failed', description: e.message || 'Unable to add school.' });
    }
  };

  const onToggleSelect = (id, checked) => {
    setSelectedIds((prev) => checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id));
  };

  const onOpenEmployee = (emp) => {
    setActiveEmployee(emp);
    setDrawerOpen(true);
  };

  const onBulkAssign = async () => {
    if (!bulkSchoolId || selectedIds.length === 0) return;
    try {
      setAssigning(true);
      await apiService.bulkAssignSchool(selectedIds, bulkSchoolId);
      const school = schools.find((s) => String(s.key || s.school_number || s.id) === String(bulkSchoolId));
      setEmployees((prev) => prev.map((e) => selectedIds.includes(e.id) ? { ...e, school_id: String(bulkSchoolId), school_name: school?.name || e.school_name } : e));
      setSelectedIds([]);
      setBulkSchoolId('');
      toast({ title: 'Assigned', description: 'Selected employees have been assigned to the school.' });
    } catch (e) {
      toast({ title: 'Assignment failed', description: e.message || 'Unable to assign school.' });
    } finally {
      setAssigning(false);
    }
  };

  const onEmployeeSaved = (updated) => {
    setEmployees((prev) => prev.map((e) => e.id === updated.id ? { ...e, ...updated } : e));
  };

  const handleQuickAction = async (actionId) => {
    try {
      switch (actionId) {
        case 'ai_chat':
          setCurrentView('aiChat');
          break;
        case 'ai_absence':
          setCurrentView('absences');
          // Could also trigger a modal here for immediate AI assistance
          toast({ 
            title: 'AI Assistant', 
            description: 'Navigate to Absences and click "AI Request" for intelligent time-off assistance' 
          });
          break;
        case 'approve_leave':
          setCurrentView('absences');
          break;
        case 'schedule_review':
          setCurrentView('performance');
          break;
        case 'new_hire':
          setCurrentView('employees');
          break;
        case 'urgent_tasks':
          toast({ title: 'Notifications', description: `You have ${unreadCount} unread notifications` });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Quick action failed:', error);
    }
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'absences', label: 'Absences', icon: Calendar },
    { id: 'observations', label: 'Observations', icon: Eye },
    { id: 'aiChat', label: 'AI Assistant', icon: MessageSquare },
    { id: 'dataSync', label: 'Data Sync', icon: Database },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <>
      {/* Auth error banner - shows when role fetching fails */}
      {authError && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-800">
                Unable to load roles from server. Some features may be limited.
              </span>
            </div>
            <span className="text-xs text-yellow-600">{authError}</span>
          </div>
        </div>
      )}
      <ResponsiveLayout
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentUser={currentUser}
        userRoles={userRoles}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
        handleQuickAction={handleQuickAction}
      >
        {currentView === 'dashboard' && (
          <DashboardView 
            stats={dashboardStats} 
            loading={loading} 
            employees={employees}
            notifications={notifications}
            unreadCount={unreadCount}
            handleQuickAction={handleQuickAction}
            onOpenEmployee={onOpenEmployee}
          />
        )}
        {currentView === 'employees' && (
          <EmployeeManagement canEdit={canEdit} />
        )}
        {currentView === 'performance' && (<PerformanceView userRoles={userRoles} employees={employees} />)}
        {currentView === 'absences' && (<AbsenceManagement userRoles={userRoles} currentUser={currentUser} />)}
        {currentView === 'observations' && (<ObservationsView userRoles={userRoles} />)}
        {currentView === 'aiChat' && (<AIChatPage />)}
        {currentView === 'dataSync' && (<DataSyncView />)}
        {currentView === 'settings' && (<SettingsView userRoles={userRoles} />)}
      </ResponsiveLayout>

      <EmployeeDrawer 
        open={drawerOpen}
        setOpen={setDrawerOpen}
        employee={activeEmployee}
        schools={schools}
        canEdit={canEdit}
        onSaved={onEmployeeSaved}
      />

      <Toaster />
      
      {/* AI Chat Widget - only show when not on AI chat page */}
      {currentView !== 'aiChat' && <AIChatWidget />}
    </>
  );
}

function DashboardView({ stats, loading, employees, notifications, unreadCount, handleQuickAction, onOpenEmployee }) {
  if (loading || !stats) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <ResponsiveGrid columns={{ sm: 1, md: 2, lg: 4 }}>
          {[...Array(4)].map((_, i) => (
            <ResponsiveCard key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </ResponsiveCard>
          ))}
        </ResponsiveGrid>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <ResponsiveGrid columns={{ sm: 1, md: 2, lg: 4 }}>
        <ResponsiveCard className="bg-gradient-to-r from-gray-900 to-black text-white border-0 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Total Employees</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.total_employees}</p>
              <p className="text-xs text-gray-400 mt-1">Active Staff</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
            </div>
          </div>
        </ResponsiveCard>

        <ResponsiveCard className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black border-0 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-900 text-sm">Pending Approvals</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.pending_absences || 0}</p>
              <p className="text-xs text-yellow-800 mt-1">Requires Action</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
            </div>
          </div>
        </ResponsiveCard>

        <ResponsiveCard className="bg-gradient-to-r from-gray-700 to-gray-800 text-white border-0 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Total Absences</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.total_absences}</p>
              <p className="text-xs text-gray-400 mt-1">This Year</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
            </div>
          </div>
        </ResponsiveCard>

        <ResponsiveCard className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-black border-0 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-900 text-sm">Performance Reviews</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.total_observations}</p>
              <p className="text-xs text-yellow-800 mt-1">This Quarter</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
            </div>
          </div>
        </ResponsiveCard>
      </ResponsiveGrid>

      {/* Quick Actions and Activity */}
      <ResponsiveGrid columns={{ sm: 1, lg: 3 }}>
        <ResponsiveCard className="bg-white border-0 shadow-lg">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-yellow-500 rounded mr-2 sm:mr-3"></div>
            Quick Actions
          </h3>
          <div className="space-y-3">
            {[
              { id: 'ai_chat', label: 'AI Assistant', icon: MessageSquare, count: 0, color: 'bg-yellow-500', isAI: true },
              { id: 'ai_absence', label: 'AI Time Off', icon: Bot, count: 0, color: 'bg-yellow-600', isAI: true },
              { id: 'approve_leave', label: 'Approve Leave', icon: CheckCircle, count: stats.pending_absences || 0, color: 'bg-green-500' },
              { id: 'schedule_review', label: 'Schedule Reviews', icon: Calendar, count: 8, color: 'bg-blue-500' },
              { id: 'new_hire', label: 'Process New Hire', icon: UserPlus, count: 2, color: 'bg-purple-500' },
              { id: 'urgent_tasks', label: 'Notifications', icon: AlertCircle, count: unreadCount, color: 'bg-red-500' }
            ].map((action) => {
              const Icon = action.icon;
              return (
                <TouchButton
                  key={action.id}
                  onClick={() => handleQuickAction(action.id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-yellow-500 hover:bg-yellow-50 transition-all bg-white"
                  variant="ghost"
                  size="default"
                >
                  <div className="flex items-center">
                    <div className={`w-8 h-8 ${action.color} rounded-lg flex items-center justify-center mr-3`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-900">{action.label}</span>
                  </div>
                  {action.count > 0 && (
                    <Badge className="bg-yellow-500 text-black text-xs">{action.count}</Badge>
                  )}
                </TouchButton>
              );
            })}
          </div>
        </ResponsiveCard>

        <ResponsiveCard className="lg:col-span-2 bg-white border-0 shadow-lg">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-800 rounded mr-2 sm:mr-3"></div>
            Recent Activity
          </h3>
          <div className="space-y-4">
            {notifications.slice(0, 5).map((notification) => (
              <div key={notification.id} className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                  notification.type === 'absence_request' ? 'bg-yellow-100' : 
                  notification.type === 'absence_approved' ? 'bg-green-100' : 
                  notification.type === 'absence_denied' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  {notification.type === 'absence_request' && <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />}
                  {notification.type === 'absence_approved' && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />}
                  {notification.type === 'absence_denied' && <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />}
                  {!notification.type.includes('absence') && <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{notification.title}</p>
                  <p className="text-xs text-gray-500 truncate">{notification.message}</p>
                </div>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${notification.read ? 'bg-gray-300' : 'bg-yellow-500'}`}></div>
              </div>
            ))}
            
            {notifications.length === 0 && (
              <div className="text-center py-8">
                <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </ResponsiveCard>
      </ResponsiveGrid>

      <ResponsiveCard className="bg-white border-0 shadow-lg">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-yellow-500 rounded mr-2 sm:mr-3"></div>
          Recent Staff Updates
        </h3>
        <ResponsiveGrid columns={{ sm: 1, md: 2, lg: 3 }} gap="default">
          {employees.slice(0, 6).map((employee, index) => (
            <TouchButton
              key={employee.id}
              className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-left w-full h-auto"
              onClick={() => onOpenEmployee(employee)}
              variant="ghost"
            >
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-xs sm:text-sm">
                    {(employee.first_name || ' ')[0]}{(employee.last_name || ' ')[0]}
                  </span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {employee.first_name} {employee.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{employee.position_title || 'Staff Member'}</p>
                <p className="text-xs text-gray-400 truncate">{employee.school_name || 'Unassigned'}</p>
              </div>
            </TouchButton>
          ))}
        </ResponsiveGrid>
      </ResponsiveCard>
    </div>
  );
}

function SettingsView({ userRoles }) {
  const { hasPermission, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('accessibility');
  
  // Check if user has admin permissions for Azure AD settings
  // Use both the new permission system and legacy role checks for backward compatibility
  const hasManageSystem = hasPermission('manage_system') || hasRole('super_admin') || hasRole('hr_admin') || userRoles.includes('Admin') || userRoles.includes('super_admin');
  
  return (
    <div className="space-y-6">
      <ResponsiveCard>
        <h2 className="text-xl font-bold mb-4">Settings</h2>
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-4 sm:gap-8">
            <button
              onClick={() => setActiveTab('accessibility')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'accessibility'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Accessibility
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'general'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              General
            </button>
            {hasManageSystem && (
              <>
                <button
                  onClick={() => setActiveTab('groupMappings')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'groupMappings'
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Group Mappings
                </button>
                <button
                  onClick={() => setActiveTab('azureConfig')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'azureConfig'
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Azure AD Config
                </button>
              </>
            )}
          </nav>
        </div>
      </ResponsiveCard>
      
      {activeTab === 'accessibility' && (
        <AccessibilitySettings />
      )}
      
      {activeTab === 'general' && (
        <ResponsiveCard>
          <h3 className="text-lg font-semibold mb-4">General Settings</h3>
          <p className="text-gray-600">General application settings will be available here.</p>
        </ResponsiveCard>
      )}
      
      {activeTab === 'groupMappings' && hasManageSystem && (
        <GroupMappingManagement />
      )}
      
      {activeTab === 'azureConfig' && hasManageSystem && (
        <AzureConfigSettings />
      )}
    </div>
  );
}

function EmployeesView({ employees, setEmployees, searchTerm, onSearch, onSearchChange, suggestions, loading, userRoles, schools, selectedSchool, onSchoolChange, addingSchool, setAddingSchool, newSchool, setNewSchool, onAddSchool, selectedIds, setSelectedIds, onToggleSelect, onOpenEmployee, onSelectSuggestion, bulkSchoolId, setBulkSchoolId, onBulkAssign, assigning }) {
  const canCreateEmployee = userRoles.includes('Admin') || userRoles.includes('HR_Staff');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    first_name: '',
    last_name: '',
    email: '',
    position_title: '',
    department: 'Teaching Staff',
    school_id: '',
    hire_date: new Date().toISOString().split('T')[0],
    fte: 1.0,
    notes: ''
  });
  const [creatingEmployee, setCreatingEmployee] = useState(false);
  const [showAILookup, setShowAILookup] = useState(false);

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      setCreatingEmployee(true);
      const employeeData = {
        ...newEmployee,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const created = await apiService.createEmployee(employeeData);
      setEmployees(prev => [created, ...prev]);
      
      setNewEmployee({
        first_name: '',
        last_name: '',
        email: '',
        position_title: '',
        department: 'Teaching Staff',
        school_id: '',
        hire_date: new Date().toISOString().split('T')[0],
        fte: 1.0,
        notes: ''
      });
      setShowAddEmployee(false);
      
      toast({ 
        title: 'Employee Added', 
        description: `${created.first_name} ${created.last_name} has been added` 
      });
    } catch (error) {
      console.error('Failed to create employee:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to create employee. Please try again.' 
      });
    } finally {
      setCreatingEmployee(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white border-0 shadow-lg">
        <form onSubmit={onSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                type="text" 
                placeholder="Search employees by name or email..." 
                value={searchTerm} 
                onChange={(e) => onSearchChange(e.target.value)} 
                className="pl-10 bg-gray-50 border-gray-200 focus:border-yellow-500" 
              />
              {suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-auto">
                  {suggestions.map((s) => (
                    <button type="button" key={s.id} onClick={() => onSelectSuggestion(s)} className="w-full text-left px-4 py-3 hover:bg-gray-50">
                      <div className="text-sm text-gray-900 font-medium">{s.first_name} {s.last_name}</div>
                      <div className="text-xs text-gray-500">{s.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <select 
                value={selectedSchool} 
                onChange={onSchoolChange} 
                className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50 focus:border-yellow-500"
              >
                <option value="">All Schools</option>
                {schools.map(s => (
                  <option key={s.id} value={s.key || s.school_number || ''}>{s.name}{s.abbreviation ? ` (${s.abbreviation})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={loading} className="bg-black hover:bg-gray-800 text-white">
                <Search className="w-4 h-4 mr-2" /> Search
              </Button>
              <Button 
                type="button" 
                onClick={() => setShowAILookup(true)}
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black"
              >
                <MessageSquare className="w-4 h-4 mr-2" /> AI Search
              </Button>
              {canCreateEmployee && (
                <Button 
                  type="button" 
                  onClick={() => setShowAddEmployee(true)}
                  variant="outline"
                  className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              )}
            </div>
          </div>
        </form>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="mt-4 p-4 border border-yellow-200 rounded-lg bg-yellow-50 flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900">{selectedIds.length} employees selected</span>
            <select 
              value={bulkSchoolId} 
              onChange={(e) => setBulkSchoolId(e.target.value)} 
              className="h-9 border border-gray-300 rounded-lg px-3 text-sm bg-white"
            >
              <option value="">Assign to school...</option>
              {schools.map((s) => (
                <option key={s.id} value={s.key || s.school_number || ''}>{s.name}{s.abbreviation ? ` (${s.abbreviation})` : ''}</option>
              ))}
            </select>
            <Button disabled={!bulkSchoolId || assigning} onClick={onBulkAssign} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
            <Button variant="outline" onClick={() => { setSelectedIds([]); setBulkSchoolId(''); }}>Clear</Button>
          </div>
        )}
      </Card>

      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <Card key={i} className="p-6 animate-pulse bg-white border-0 shadow-lg">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          employees.map((employee) => (
            <Card key={employee.id} className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer group" onClick={() => onOpenEmployee(employee)}>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <input 
                      type="checkbox" 
                      className="h-4 w-4 text-yellow-500 border-gray-300 rounded" 
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleSelect(employee.id, e.target.checked);
                      }} 
                      checked={selectedIds?.includes(employee.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center border-4 border-gray-100">
                        <span className="text-white font-bold text-lg">
                          {(employee.first_name || ' ')[0]}{(employee.last_name || ' ')[0]}
                        </span>
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                        employee.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                    </div>
                  </div>
                  <Badge className={`${
                    employee.status === 'active' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {employee.status}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-yellow-600 transition-colors">
                    {employee.first_name || '—'} {employee.last_name || ''}
                  </h3>
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {employee.email || '—'}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Award className="w-4 h-4 mr-2 text-gray-400" />
                      {employee.position_title || '—'} • {employee.department || '—'}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                      {employee.school_name || employee.school_id || '—'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-200 hover:border-yellow-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (employee.email) {
                          window.open(`mailto:${employee.email}`, '_self');
                        }
                      }}
                    >
                      <Mail className="w-3 h-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-200 hover:border-yellow-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        toast({ title: 'Calendar', description: 'Calendar integration coming soon' });
                      }}
                    >
                      <Calendar className="w-3 h-3" />
                    </Button>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                    Active
                  </Badge>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {showAddEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl bg-white border-0 shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Add New Employee</h3>
                <Button variant="outline" size="sm" onClick={() => setShowAddEmployee(false)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleCreateEmployee} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">First Name *</Label>
                    <Input 
                      value={newEmployee.first_name} 
                      onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Last Name *</Label>
                    <Input 
                      value={newEmployee.last_name} 
                      onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-700">Email Address *</Label>
                    <Input 
                      type="email"
                      value={newEmployee.email} 
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Position Title</Label>
                    <Input 
                      value={newEmployee.position_title} 
                      onChange={(e) => setNewEmployee({...newEmployee, position_title: e.target.value})}
                      placeholder="e.g., Mathematics Teacher"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Department</Label>
                    <select 
                      value={newEmployee.department} 
                      onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                      className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white"
                    >
                      <option value="Teaching Staff">Teaching Staff</option>
                      <option value="Administration">Administration</option>
                      <option value="Support Staff">Support Staff</option>
                      <option value="Counseling">Counseling</option>
                      <option value="Special Education">Special Education</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">School</Label>
                    <select 
                      value={newEmployee.school_id} 
                      onChange={(e) => setNewEmployee({...newEmployee, school_id: e.target.value})}
                      className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white"
                    >
                      <option value="">No school assigned</option>
                      {schools.map(s => (
                        <option key={s.id} value={s.key || s.school_number || ''}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Hire Date</Label>
                    <Input 
                      type="date"
                      value={newEmployee.hire_date} 
                      onChange={(e) => setNewEmployee({...newEmployee, hire_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowAddEmployee(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={creatingEmployee || !newEmployee.first_name || !newEmployee.last_name || !newEmployee.email}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    {creatingEmployee ? 'Adding...' : 'Add Employee'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* AI Lookup Modal */}
      {showAILookup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <AIAssistedEmployeeLookup
            onEmployeeSelected={(employee) => {
              setShowAILookup(false);
              onOpenEmployee(employee);
            }}
            onClose={() => setShowAILookup(false)}
          />
        </div>
      )}
    </div>
  );
}

function AbsencesView({ userRoles }) {
  const [absences, setAbsences] = useState([]);
  const [pendingAbsences, setPendingAbsences] = useState([]);
  const [absenceStats, setAbsenceStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    employee_id: ''
  });

  const [newAbsence, setNewAbsence] = useState({
    employee_id: '',
    leave_type: 'PTO',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    total_hours: 8,
    total_minutes: 0,
    purpose: '',
    notes: '',
    requires_documentation: false,
    documentation_url: '',
    unpaid_hours: 0,
    unpaid_minutes: 0,
    pto_hours: 0,
    grace_period_used: false
  });

  const [creatingAbsence, setCreatingAbsence] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const canManageAbsences = userRoles.includes('Admin') || userRoles.includes('HR_Staff');

  useEffect(() => {
    loadAbsenceData();
    loadEmployees();
  }, [filters]);

  const loadAbsenceData = async () => {
    try {
      setLoading(true);
      const [absencesList, pendingList, stats] = await Promise.all([
        apiService.getAbsences(0, 100, filters),
        canManageAbsences ? apiService.getPendingAbsences() : Promise.resolve([]),
        apiService.getAbsenceStats()
      ]);
      setAbsences(absencesList);
      setPendingAbsences(pendingList);
      setAbsenceStats(stats);
    } catch (error) {
      console.error('Failed to load absence data:', error);
      toast({ title: 'Error', description: 'Failed to load absence data' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const employeesList = await apiService.getEmployees(0, 500);
      setEmployees(employeesList);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const calculateTotalTime = () => {
    if (!newAbsence.start_date || !newAbsence.end_date) return;
    
    const start = new Date(`${newAbsence.start_date}T${newAbsence.start_time || '08:00'}`);
    const end = new Date(`${newAbsence.end_date}T${newAbsence.end_time || '16:00'}`);
    
    if (newAbsence.start_date === newAbsence.end_date && newAbsence.start_time && newAbsence.end_time) {
      // Same day partial absence
      const diffMs = end.getTime() - start.getTime();
      const totalMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      setNewAbsence(prev => ({
        ...prev,
        total_hours: hours,
        total_minutes: minutes,
        pto_hours: newAbsence.leave_type === 'PTO' ? hours : 0,
        unpaid_hours: newAbsence.leave_type === 'UNPAID' ? hours : 0,
        unpaid_minutes: newAbsence.leave_type === 'UNPAID' ? minutes : 0
      }));
    } else {
      // Multi-day absence
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const totalHours = diffDays * 8;
      
      setNewAbsence(prev => ({
        ...prev,
        total_hours: totalHours,
        total_minutes: 0,
        pto_hours: newAbsence.leave_type === 'PTO' ? totalHours : 0,
        unpaid_hours: newAbsence.leave_type === 'UNPAID' ? totalHours : 0,
        unpaid_minutes: 0
      }));
    }
  };

  useEffect(() => {
    calculateTotalTime();
  }, [newAbsence.start_date, newAbsence.end_date, newAbsence.start_time, newAbsence.end_time, newAbsence.leave_type]);

  const handleCreateAbsence = async (e) => {
    e.preventDefault();
    try {
      setCreatingAbsence(true);
      await apiService.createAbsence(newAbsence);
      setShowCreateForm(false);
      resetNewAbsenceForm();
      await loadAbsenceData();
      toast({ title: 'Success', description: 'Absence request created successfully' });
      
      // Refresh notifications after creating absence
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
      setTimeout(async () => {
        try {
          await apiService.get('/api/notifications/unread-count');
        } catch (err) {
          console.error('Failed to refresh notification count:', err);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to create absence:', error);
      toast({ title: 'Error', description: 'Failed to create absence request' });
    } finally {
      setCreatingAbsence(false);
    }
  };

  const handleUpdateAbsence = async (e) => {
    e.preventDefault();
    if (!editingAbsence) return;
    
    try {
      setCreatingAbsence(true);
      await apiService.updateAbsence(editingAbsence.id, newAbsence);
      setShowCreateForm(false);
      setEditingAbsence(null);
      resetNewAbsenceForm();
      await loadAbsenceData();
      toast({ title: 'Success', description: 'Absence request updated successfully' });
    } catch (error) {
      console.error('Failed to update absence:', error);
      toast({ title: 'Error', description: 'Failed to update absence request' });
    } finally {
      setCreatingAbsence(false);
    }
  };

  const resetNewAbsenceForm = () => {
    setNewAbsence({
      employee_id: '',
      leave_type: 'PTO',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      total_hours: 8,
      total_minutes: 0,
      purpose: '',
      notes: '',
      requires_documentation: false,
      documentation_url: '',
      unpaid_hours: 0,
      unpaid_minutes: 0,
      pto_hours: 0,
      grace_period_used: false
    });
  };

  const handleApproveAbsence = async (absenceId, action, disciplineAction = '') => {
    console.log('=== APPROVAL PROCESS STARTED ===');
    console.log('Absence ID:', absenceId);
    console.log('Action:', action);
    console.log('Discipline Action:', disciplineAction);
    
    try {
      // Validate authentication before proceeding
      console.log('🔍 Validating authentication...');
      const authValidation = await AuthValidation.runFullValidation();
      
      if (!authValidation.tokenAcquisition) {
        throw new Error('Authentication validation failed. Please sign in again.');
      }
      
      console.log('✅ Authentication validated');
      
      const updateData = { 
        status: action,
        approval_comments: disciplineAction || `${action === 'approved' ? 'Approved' : 'Denied'} by HR Staff`,
        discipline_action: disciplineAction || undefined
      };
      
      console.log('📤 Sending update data:', updateData);
      console.log('🎯 API endpoint:', `/api/absences/${absenceId}`);
      
      const result = await apiService.updateAbsence(absenceId, updateData);
      console.log('✅ Update successful:', result);
      
      // Reload absence data
      await loadAbsenceData();
      console.log('✅ Data reloaded');
      
      toast({ 
        title: '✅ Success', 
        description: `Absence request ${action === 'approved' ? 'approved' : 'denied'}${disciplineAction ? ' with discipline action' : ''}` 
      });
      
    } catch (error) {
      console.error('❌ APPROVAL FAILED:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Provide specific error messages based on error type
      let errorMessage = 'Unable to process approval. Please try again.';
      
      if (error.message.includes('Authentication') || error.message.includes('401') || error.message.includes('403')) {
        errorMessage = 'Authentication failed. Please sign out and sign in again.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Absence record not found. It may have been deleted or modified.';
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: '❌ Approval Failed', 
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const exportAbsenceData = () => {
    const csvHeaders = [
      'Date of Actual Leave',
      'Employee Name', 
      'Purpose of Request',
      'Leave Type',
      'Total Time Off Hours',
      'Total Time Off Minutes',
      'Unpaid Hours',
      'Unpaid Minutes', 
      'PTO Hours',
      'Bereavement',
      'Jury Duty',
      'Grace Period (10 min)',
      'Medical/Doctor Documentation',
      'Discipline Action',
      'Status'
    ];
    
    const csvData = absences.map(absence => [
      absence.start_date,
      absence.employee_name,
      absence.purpose,
      absence.leave_type,
      absence.total_hours,
      absence.total_minutes,
      absence.leave_type === 'UNPAID' ? absence.total_hours : 0,
      absence.leave_type === 'UNPAID' ? absence.total_minutes : 0,
      absence.leave_type === 'PTO' ? absence.total_hours : 0,
      absence.leave_type === 'BEREAVEMENT' ? absence.total_hours : 0,
      absence.leave_type === 'JURY_DUTY' ? absence.total_hours : 0,
      absence.grace_period_used ? 'Yes' : 'No',
      absence.requires_documentation ? 'Required' : 'Not Required',
      absence.discipline_action || '',
      absence.status
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const leaveTypeOptions = [
    { value: 'PTO', label: 'PTO', color: 'bg-green-100 text-green-800' },
    { value: 'UNPAID', label: 'UNPAID', color: 'bg-red-100 text-red-800' },
    { value: 'SICK', label: 'SICK', color: 'bg-blue-100 text-blue-800' },
    { value: 'BEREAVEMENT', label: 'BEREAVEMENT', color: 'bg-purple-100 text-purple-800' },
    { value: 'JURY_DUTY', label: 'JURY DUTY', color: 'bg-gray-100 text-gray-800' },
    { value: 'MEDICAL', label: 'MEDICAL', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'PERSONAL', label: 'PERSONAL', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'EMERGENCY', label: 'EMERGENCY', color: 'bg-orange-100 text-orange-800' }
  ];

  const getLeaveTypeDisplay = (type) => {
    const option = leaveTypeOptions.find(opt => opt.value === type);
    return option || { value: type, label: type, color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Attendance Management</h2>
          <p className="text-sm text-gray-500 mt-1">Track employee absences, PTO, and leave requests</p>
          {/* Debug info for troubleshooting */}
          <p className="text-xs text-gray-400 mt-1">
            Your roles: {userRoles.length > 0 ? userRoles.join(', ') : 'No roles assigned'} | 
            Can manage: {canManageAbsences ? 'Yes' : 'No'}
          </p>
        </div>
        <div className="flex space-x-3">
          <Button 
            onClick={exportAbsenceData}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
          {/* Test button to debug auth */}
          <Button 
            onClick={async () => {
              try {
                const token = await apiService.getAccessToken();
                console.log('Auth token:', token);
                toast({ title: 'Auth Test', description: 'Token acquired successfully. Check console for details.' });
              } catch (error) {
                console.error('Auth test failed:', error);
                toast({ title: 'Auth Test Failed', description: error.message });
              }
            }}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Bug className="w-4 h-4 mr-2" /> Test Auth
          </Button>
          {/* Show button for everyone temporarily for testing */}
          <Button 
            onClick={() => setShowAIAssistant(true)}
            className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black"
          >
            <MessageSquare className="w-4 h-4 mr-2" /> AI Request
          </Button>
          <Button 
            onClick={() => setShowCreateForm(true)}
            variant="outline"
            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
          >
            <Plus className="w-4 h-4 mr-2" /> Manual Form
          </Button>
        </div>
      </div>

      {/* Quick Stats Dashboard */}
      {absenceStats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black border-0 shadow-lg">
            <div className="text-center">
              <p className="text-yellow-900 text-xs font-medium">PENDING</p>
              <p className="text-2xl font-bold">{absenceStats.pending_requests}</p>
              <p className="text-xs text-yellow-800">Requests</p>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white border-0 shadow-lg">
            <div className="text-center">
              <p className="text-green-100 text-xs font-medium">PTO USED</p>
              <p className="text-2xl font-bold">{absenceStats.leave_type_breakdown?.PTO?.total_hours || 0}</p>
              <p className="text-xs text-green-200">Hours</p>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-r from-red-500 to-red-600 text-white border-0 shadow-lg">
            <div className="text-center">
              <p className="text-red-100 text-xs font-medium">UNPAID</p>
              <p className="text-2xl font-bold">{absenceStats.leave_type_breakdown?.UNPAID?.total_hours || 0}</p>
              <p className="text-xs text-red-200">Hours</p>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <div className="text-center">
              <p className="text-blue-100 text-xs font-medium">SICK DAYS</p>
              <p className="text-2xl font-bold">{absenceStats.leave_type_breakdown?.SICK?.count || 0}</p>
              <p className="text-xs text-blue-200">Days</p>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-r from-black to-gray-800 text-white border-0 shadow-lg">
            <div className="text-center">
              <p className="text-gray-300 text-xs font-medium">TOTAL</p>
              <p className="text-2xl font-bold">{absenceStats.total_absences}</p>
              <p className="text-xs text-gray-400">Absences</p>
            </div>
          </Card>
        </div>
      )}

      {/* Pending Approvals Section */}
      {canManageAbsences && pendingAbsences.length > 0 && (
        <Card className="p-6 bg-white border-l-4 border-l-yellow-500 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 text-yellow-500 mr-2" />
              Pending Approvals ({pendingAbsences.length})
            </h3>
            <Badge className="bg-yellow-500 text-black">Action Required</Badge>
          </div>
          
          <div className="space-y-3">
            {pendingAbsences.map((absence) => (
              <div key={absence.id} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {absence.employee_name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{absence.employee_name}</h4>
                    <p className="text-sm text-gray-600">
                      <Badge className={getLeaveTypeDisplay(absence.leave_type).color} size="sm">
                        {getLeaveTypeDisplay(absence.leave_type).label}
                      </Badge>
                      <span className="ml-2">{absence.start_date} to {absence.end_date}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>Purpose:</strong> {absence.purpose} • <strong>Duration:</strong> {absence.total_hours}h {absence.total_minutes}m
                      {absence.requires_documentation && (
                        <span className="ml-2 text-red-600">• Documentation Required</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={() => {
                        console.log('🟢 APPROVE BUTTON CLICKED');
                        console.log('Absence ID:', absence.id);
                        console.log('Function exists:', typeof handleApproveAbsence);
                        handleApproveAbsence(absence.id, 'approved');
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-red-500 hover:bg-red-600 text-white"
                      onClick={() => {
                        console.log('🔴 DENY BUTTON CLICKED');
                        console.log('Absence ID:', absence.id);
                        console.log('Function exists:', typeof handleApproveAbsence);
                        handleApproveAbsence(absence.id, 'denied');
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Deny
                    </Button>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={() => {
                      console.log('🟠 DISCIPLINE BUTTON CLICKED');
                      const disciplineAction = prompt('Enter discipline action (optional):');
                      if (disciplineAction !== null) {
                        console.log('Discipline action entered:', disciplineAction);
                        handleApproveAbsence(absence.id, 'approved', disciplineAction);
                      }
                    }}
                  >
                    Approve w/ Discipline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-6 bg-white border-0 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Filter Attendance Records</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">Employee</Label>
            <select 
              value={filters.employee_id} 
              onChange={(e) => setFilters({...filters, employee_id: e.target.value})}
              className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:border-yellow-500"
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Status</Label>
            <select 
              value={filters.status} 
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:border-yellow-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
            </select>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Leave Type</Label>
            <select 
              value={filters.leave_type} 
              onChange={(e) => setFilters({...filters, leave_type: e.target.value})}
              className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:border-yellow-500"
            >
              <option value="">All Types</option>
              {leaveTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">From Date</Label>
            <Input 
              type="date" 
              value={filters.start_date} 
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">To Date</Label>
            <Input 
              type="date" 
              value={filters.end_date} 
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {/* Attendance Records Table */}
      <Card className="p-6 bg-white border-0 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900">Attendance Records</h3>
          <div className="flex items-center space-x-2">
            <Badge variant="outline" className="text-xs">
              {absences.length} Total Records
            </Badge>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 animate-pulse bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Date of Absence</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Employee</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Purpose</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Leave Type</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Total Time</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Unpaid</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">PTO</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Documentation</th>
                  <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                  {canManageAbsences && <th className="text-left p-3 text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {absences.map((absence) => {
                  const leaveType = getLeaveTypeDisplay(absence.leave_type);
                  const isPTO = absence.leave_type === 'PTO';
                  const isUnpaid = absence.leave_type === 'UNPAID';
                  
                  return (
                    <tr key={absence.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="text-sm font-medium text-gray-900">
                          {absence.start_date === absence.end_date ? (
                            <div>
                              <div>{new Date(absence.start_date).toLocaleDateString()}</div>
                              {absence.start_time && (
                                <div className="text-xs text-gray-500">{absence.start_time} - {absence.end_time}</div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div>{new Date(absence.start_date).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">to {new Date(absence.end_date).toLocaleDateString()}</div>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-xs">
                              {absence.employee_name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{absence.employee_name}</p>
                            <p className="text-xs text-gray-500">{absence.school_name || 'Unassigned'}</p>
                          </div>
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="text-sm text-gray-900 max-w-xs">
                          <div className="truncate font-medium" title={absence.purpose}>
                            {absence.purpose}
                          </div>
                          {absence.notes && (
                            <div className="text-xs text-gray-500 mt-1 truncate" title={absence.notes}>
                              {absence.notes}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <Badge className={leaveType.color} size="sm">
                          {leaveType.label}
                        </Badge>
                      </td>
                      
                      <td className="p-3">
                        <div className="text-sm font-medium text-gray-900">
                          {absence.total_hours}h{absence.total_minutes > 0 ? ` ${absence.total_minutes}m` : ''}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="text-sm font-medium text-gray-900">
                          {isUnpaid ? `${absence.total_hours}h${absence.total_minutes > 0 ? ` ${absence.total_minutes}m` : ''}` : '0h'}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="text-sm font-medium text-gray-900">
                          {isPTO ? `${absence.total_hours}h` : '0h'}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="text-center">
                          {absence.requires_documentation ? (
                            <div className="flex flex-col items-center space-y-1">
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">Required</Badge>
                              {absence.documentation_url ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Not Required</span>
                          )}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="flex flex-col space-y-1">
                          <Badge className={
                            absence.status === 'approved' ? 'bg-green-100 text-green-800' :
                            absence.status === 'denied' ? 'bg-red-100 text-red-800' :
                            absence.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          } size="sm">
                            {absence.status.toUpperCase()}
                          </Badge>
                          {absence.discipline_action && (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">
                              Discipline
                            </Badge>
                          )}
                        </div>
                      </td>
                      
                      {canManageAbsences && (
                        <td className="p-3">
                          {absence.status === 'pending' ? (
                            <div className="flex space-x-1">
                              <Button 
                                size="sm" 
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => {
                                  console.log('🟢 TABLE APPROVE CLICKED');
                                  console.log('Absence ID:', absence.id);
                                  handleApproveAbsence(absence.id, 'approved');
                                }}
                              >
                                ✓
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-red-500 hover:bg-red-600 text-white"
                                onClick={() => {
                                  console.log('🔴 TABLE DENY CLICKED');
                                  console.log('Absence ID:', absence.id);
                                  handleApproveAbsence(absence.id, 'denied');
                                }}
                              >
                                ✗
                              </Button>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">
                              {absence.approved_by && `Approved by ${absence.approved_by}`}
                              {absence.status === 'approved' && <div className="text-green-600">✓ Approved</div>}
                              {absence.status === 'denied' && <div className="text-red-600">✗ Denied</div>}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {absences.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Absence Records</h3>
                <p className="text-gray-500 mb-4">No absence records found for the current filters</p>
                <Button 
                  onClick={() => setFilters({ status: '', leave_type: '', start_date: '', end_date: '', employee_id: '' })}
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Create Absence Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl bg-white border-0 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Record Employee Absence</h3>
                  <p className="text-sm text-gray-500">Complete attendance tracking form</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={editingAbsence ? handleUpdateAbsence : handleCreateAbsence} className="space-y-6">
                {/* Employee & Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="md:col-span-2">
                    <Label className="text-sm font-bold text-gray-700">Employee Selection *</Label>
                    <div className="mt-1">
                      <EmployeeCombobox
                        employees={employees}
                        value={newAbsence.employee_id || ""}
                        onValueChange={(value) => setNewAbsence({...newAbsence, employee_id: value})}
                        placeholder="Select Employee"
                        emptyMessage="No employees available"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-bold text-gray-700">Leave Type *</Label>
                    <select 
                      value={newAbsence.leave_type} 
                      onChange={(e) => setNewAbsence({...newAbsence, leave_type: e.target.value})}
                      className="mt-1 w-full h-10 border border-gray-300 rounded-lg px-3 text-sm bg-white focus:border-yellow-500"
                      required
                    >
                      {leaveTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">Purpose/Request Reason *</Label>
                    <Input 
                      value={newAbsence.purpose} 
                      onChange={(e) => setNewAbsence({...newAbsence, purpose: e.target.value})}
                      placeholder="e.g., Doctor appointment, Personal emergency"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Date & Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">Start Date *</Label>
                    <Input 
                      type="date" 
                      value={newAbsence.start_date} 
                      onChange={(e) => setNewAbsence({...newAbsence, start_date: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">End Date *</Label>
                    <Input 
                      type="date" 
                      value={newAbsence.end_date} 
                      onChange={(e) => setNewAbsence({...newAbsence, end_date: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">Start Time</Label>
                    <Input 
                      type="time" 
                      value={newAbsence.start_time} 
                      onChange={(e) => setNewAbsence({...newAbsence, start_time: e.target.value})}
                      className="mt-1"
                      placeholder="08:00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank for full day</p>
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">End Time</Label>
                    <Input 
                      type="time" 
                      value={newAbsence.end_time} 
                      onChange={(e) => setNewAbsence({...newAbsence, end_time: e.target.value})}
                      className="mt-1"
                      placeholder="16:00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank for full day</p>
                  </div>
                </div>

                {/* Time Calculations */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-yellow-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">Total Hours</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="24"
                      step="0.5"
                      value={newAbsence.total_hours} 
                      onChange={(e) => setNewAbsence({...newAbsence, total_hours: parseFloat(e.target.value) || 0})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-bold text-gray-700">Total Minutes</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="59"
                      value={newAbsence.total_minutes} 
                      onChange={(e) => setNewAbsence({...newAbsence, total_minutes: parseInt(e.target.value) || 0})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">
                      {newAbsence.leave_type === 'PTO' ? 'PTO Hours Used' : 'Unpaid Hours'}
                    </Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={newAbsence.leave_type === 'PTO' ? newAbsence.total_hours : (newAbsence.leave_type === 'UNPAID' ? newAbsence.total_hours : 0)} 
                      readOnly
                      className="mt-1 bg-gray-100"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">Grace Period (10 min)</Label>
                    <label className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={newAbsence.grace_period_used}
                        onChange={(e) => setNewAbsence({...newAbsence, grace_period_used: e.target.checked})}
                        className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                      />
                      <span className="text-sm text-gray-700 ml-2">Used grace period</span>
                    </label>
                  </div>
                </div>

                {/* Documentation & Special Circumstances */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-red-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-bold text-gray-700">Medical/Doctor Documentation</Label>
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newAbsence.requires_documentation}
                          onChange={(e) => setNewAbsence({...newAbsence, requires_documentation: e.target.checked})}
                          className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700 ml-2">Documentation required</span>
                      </label>
                      
                      {newAbsence.requires_documentation && (
                        <Input
                          type="url"
                          placeholder="Documentation URL or file reference"
                          value={newAbsence.documentation_url}
                          onChange={(e) => setNewAbsence({...newAbsence, documentation_url: e.target.value})}
                          className="mt-1"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-bold text-gray-700">Additional Notes</Label>
                    <textarea
                      value={newAbsence.notes}
                      onChange={(e) => setNewAbsence({...newAbsence, notes: e.target.value})}
                      rows={3}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-yellow-500 focus:ring-yellow-500"
                      placeholder="Additional details, coverage arrangements, etc."
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    <strong>Summary:</strong> {newAbsence.leave_type} absence for {newAbsence.total_hours}h {newAbsence.total_minutes}m
                    {newAbsence.requires_documentation && ' (Documentation required)'}
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={creatingAbsence || !newAbsence.employee_id || !newAbsence.start_date || !newAbsence.purpose}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      {creatingAbsence ? 'Recording...' : 'Record Absence'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* AI Assistant Modal */}
      {showAIAssistant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <AIAssistedAbsenceRequest
            onRequestCreated={(request) => {
              setShowAIAssistant(false);
              loadAbsenceData();
              toast({
                title: 'Request Created',
                description: 'Your absence request has been submitted successfully.'
              });
            }}
            onClose={() => setShowAIAssistant(false)}
          />
        </div>
      )}
    </div>
  );
}

function PerformanceView({ userRoles, employees }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [topPerformers, setTopPerformers] = useState([]);

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch performance metrics and top performers in parallel
      const [metricsData, performersData] = await Promise.all([
        apiService.getPerformanceMetrics(),
        apiService.getTopPerformers(6)
      ]);
      
      setMetrics(metricsData);
      setTopPerformers(performersData);
    } catch (err) {
      console.error('Failed to load performance data:', err);
      setError(err.message || 'Failed to load performance data');
      toast({
        title: 'Error',
        description: 'Failed to load performance data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
        <Card className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-4 bg-gray-100 rounded-lg">
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6 bg-red-50 border-red-200">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-semibold text-red-800">Error Loading Performance Data</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
          <Button 
            onClick={loadPerformanceData} 
            className="mt-4 bg-red-600 hover:bg-red-700 text-white"
          >
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // Calculate stats from metrics
  const avgRating = metrics?.average_rating || 0;
  const totalObservations = metrics?.total_observations || 0;
  const formalObservations = metrics?.formal_observations || 0;
  const employeesWithObs = metrics?.employees_with_observations || 0;
  const avgScore = metrics?.average_performance_score || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 bg-gradient-to-r from-green-500 to-green-600 text-white border-0 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Average Rating</p>
              <p className="text-3xl font-bold">{avgRating ? avgRating.toFixed(1) : 'N/A'}</p>
            </div>
            <Star className="w-8 h-8 text-green-200" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-r from-black to-gray-800 text-white border-0 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Total Observations</p>
              <p className="text-3xl font-bold">{totalObservations}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-yellow-400" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black border-0 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-900 text-sm">Formal Reviews</p>
              <p className="text-3xl font-bold">{formalObservations}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-800" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-r from-gray-700 to-gray-800 text-white border-0 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-300 text-sm">Avg Performance Score</p>
              <p className="text-3xl font-bold">{avgScore.toFixed(0)}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-yellow-400" />
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-white border-0 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">Top Performers</h3>
          <Badge className="bg-gray-100 text-gray-700">{employeesWithObs} employees with observations</Badge>
        </div>
        {topPerformers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topPerformers.map((performer) => (
              <div key={performer.employee_id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                      <span className="text-black font-bold">
                        {(performer.employee_name || '  ').split(' ').map(n => n[0] || '').join('').slice(0, 2)}
                      </span>
                    </div>
                    {performer.rank <= 3 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-xs">#{performer.rank}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{performer.employee_name}</h4>
                    <p className="text-sm text-gray-600">{performer.position_title || 'Staff'}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        Score: {performer.performance_score}%
                      </Badge>
                      {performer.average_rating && (
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                          Rating: {performer.average_rating}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No performance data available yet</p>
            <p className="text-sm text-gray-400 mt-2">Start recording observations to see top performers</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function ObservationsView({ userRoles }) {
  const [showAIObservation, setShowAIObservation] = useState(false);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Teacher Observations</h2>
        {(userRoles.includes('Admin') || userRoles.includes('HR_Staff') || userRoles.includes('Observer')) && (
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowAIObservation(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black"
            >
              <MessageSquare className="w-4 h-4 mr-2" /> AI Observation
            </Button>
            <Button 
              variant="outline"
              className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
            >
              <Plus className="w-4 h-4 mr-2" /> Manual Form
            </Button>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-8 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-black" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Assisted Observations</h3>
            <p className="text-gray-600 mb-4">
              Record observations using natural language. The AI will help categorize and analyze your observations automatically.
            </p>
            <Button 
              onClick={() => setShowAIObservation(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Bot className="w-4 h-4 mr-2" /> Start AI Observation
            </Button>
          </div>
        </Card>

        <Card className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Traditional Forms</h3>
            <p className="text-gray-600 mb-4">
              Use structured forms for formal observations with rating scales and detailed criteria.
            </p>
            <Button 
              variant="outline"
              className="border-gray-600 text-gray-700 hover:bg-gray-100"
            >
              <Plus className="w-4 h-4 mr-2" /> Manual Form
            </Button>
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-white border-0 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Observations</h3>
        <div className="text-center py-8">
          <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No observations recorded yet</p>
          <p className="text-sm text-gray-400 mt-2">Start by creating your first observation using AI assistance</p>
        </div>
      </Card>

      {/* AI Observation Modal */}
      {showAIObservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <AIAssistedObservation
            onObservationCreated={(observation) => {
              setShowAIObservation(false);
              toast({
                title: 'Observation Recorded',
                description: 'Your observation has been saved successfully.'
              });
            }}
            onClose={() => setShowAIObservation(false)}
          />
        </div>
      )}
    </div>
  );
}

function DataSyncView() {
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load sync status and history on mount
  useEffect(() => {
    loadSyncStatus();
    loadSyncHistory();
  }, []);

  const loadSyncStatus = async () => {
    try {
      const status = await apiService.getSyncStatus();
      setSyncStatus(status);
    } catch (e) {
      console.error('Failed to load sync status:', e);
    }
  };

  const loadSyncHistory = async () => {
    try {
      setLoadingHistory(true);
      const historyData = await apiService.getSyncHistory(10);
      setSyncHistory(historyData.history || []);
    } catch (e) {
      console.error('Failed to load sync history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCheck = async () => {
    try {
      setChecking(true); 
      setError(null);
      const res = await apiService.syncCheck();
      setResult({ ok: res.ok, message: 'Connection successful', tables: res.tables });
    } catch (e) { 
      setError(e.message); 
    } finally { 
      setChecking(false); 
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true); 
      setError(null); 
      setResult(null);
      const res = await apiService.syncRun({ sample_limit: 100 });
      setResult(res);
      // Reload history after sync
      await loadSyncHistory();
      await loadSyncStatus();
    } catch (e) { 
      setError(e.message); 
    } finally { 
      setSyncing(false); 
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'success': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'error': 'bg-red-100 text-red-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'running': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">ClassLink Data Sync</h2>
      </div>

      {/* Sync Status Card */}
      {syncStatus && (
        <Card className="p-6 bg-white border-0 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Health Status</div>
              <div className="text-lg font-medium text-gray-900 capitalize">
                {syncStatus.health?.status || 'Unknown'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Last Sync</div>
              <div className="text-lg font-medium text-gray-900">
                {formatDate(syncStatus.health?.last_sync)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Last Updated</div>
              <div className="text-lg font-medium text-gray-900">
                {formatDate(syncStatus.timestamp)}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Actions Card */}
      <Card className="p-6 bg-white border-0 shadow-lg space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Sync Actions</h3>
        <div className="flex items-center space-x-4">
          <Button onClick={handleCheck} disabled={checking} className="bg-black hover:bg-gray-800 text-white">
            <Database className="w-4 h-4 mr-2" /> {checking ? 'Checking...' : 'Check Connection'}
          </Button>
          <Button onClick={handleSync} disabled={syncing} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
          <Button onClick={loadSyncHistory} disabled={loadingHistory} variant="outline">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} /> Refresh History
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          </div>
        )}
        
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-green-800">Operation Completed</span>
            </div>
            <pre className="text-xs text-green-700 bg-green-100 p-3 rounded overflow-auto max-h-48">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </Card>

      {/* Sync History Card */}
      <Card className="p-6 bg-white border-0 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sync History</h3>
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCcw className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading history...</span>
          </div>
        ) : syncHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No sync history available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sync ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Errors</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {syncHistory.map((sync, index) => (
                  <tr key={sync.sync_id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                      {sync.sync_id ? sync.sync_id.substring(0, 8) + '...' : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(sync.status)}`}>
                        {sync.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(sync.start_time || sync.started_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(sync.end_time || sync.completed_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {sync.total_records_processed ?? sync.records_processed ?? 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(sync.total_errors ?? sync.errors ?? 0) > 0 ? (
                        <span className="text-red-600 font-medium">{sync.total_errors ?? sync.errors}</span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// Temporary placeholder for EmployeeDrawer component
function EmployeeDrawer({ open, setOpen, employee, schools, canEdit, onSaved }) {
  return (
    <div style={{display: open ? 'block' : 'none'}}>
      <div style={{
        position: 'fixed', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)',
        background: 'white', 
        padding: '20px', 
        border: '1px solid #ccc',
        borderRadius: '8px',
        zIndex: 1000
      }}>
        <h3>Employee Details</h3>
        <p>{employee ? `${employee.first_name} ${employee.last_name}` : 'No employee selected'}</p>
        <button onClick={() => setOpen(false)}>Close</button>
      </div>
      {open && <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 999
      }} onClick={() => setOpen(false)}></div>}
    </div>
  );
}

export default App;
